import {
	AlertTriangle,
	ArrowRight,
	Camera,
	CheckCircle2,
	ChevronDown,
	ChevronUp,
	Clock,
	Download,
	Info,
	Loader2,
	Minus,
	Target,
	Trash2,
	TrendingDown,
	TrendingUp,
	X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { toast } from "sonner";
import type { ForecastSnapshotResponseApi } from "@/api/models/forecastSnapshot";
import type {
	CategoryVarianceResponseApi,
	VarianceAnalysisResponseApi,
	VarianceDataPointResponseApi,
} from "@/api/models/varianceAnalysis";
import { PermissionGate } from "@/components/permissions/PermissionGate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "@/contexts/AppContext";
import { useFormatting } from "@/hooks/useFormatting";
import { usePermissions } from "@/hooks/usePermissions";
import {
	useCreateForecastSnapshot,
	useDeleteForecastSnapshot,
	useForecastSnapshots,
	useVarianceAnalysis,
} from "@/hooks/useVarianceAnalysis";
import {
	getCashMovementTerms,
	getForecastTerm,
} from "@/lib/terminology-config";
import { getCurrencySymbol } from "@/utils/currency";

type Period = "7" | "30" | "90";

const FORECAST_PERIOD_OPTIONS = [
	{ key: "90d", label: "90 Days" },
	{ key: "6m", label: "6 Months" },
	{ key: "12m", label: "12 Months" },
	{ key: "3y", label: "3 Years" },
];

function mapVariancePeriodToForecastKey(period: string): string {
	switch (period) {
		case "7":
			return "90d";
		case "30":
			return "90d";
		case "90":
			return "90d";
		default:
			return "90d";
	}
}

function formatSnapshotDate(isoString: string): string {
	return new Date(isoString).toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
	});
}

function getVarianceStatus(
	variance: number,
	categoryType: string,
): "favourable" | "adverse" | "on-track" {
	if (Math.abs(variance) < 100) return "on-track";
	if (categoryType === "inflow") {
		return variance > 0 ? "favourable" : "adverse";
	}
	return variance > 0 ? "adverse" : "favourable";
}

function exportCategoriesCsv(categories: CategoryVarianceResponseApi[]) {
	const header =
		"Category,Type,Actual,Forecasted,Budget,Forecast Variance,Budget Variance,Accuracy %";
	const rows = categories.map((c) =>
		[
			c.categoryName,
			c.categoryType,
			c.actualAmount,
			c.forecastedAmount,
			c.budgetAmount ?? "",
			c.forecastVariance,
			c.budgetVariance ?? "",
			c.forecastAccuracyPercent,
		].join(","),
	);
	const csv = [header, ...rows].join("\n");
	const blob = new Blob([csv], { type: "text/csv" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = "variance-analysis.csv";
	a.click();
	URL.revokeObjectURL(url);
}

function ChartTooltip({
	active,
	payload,
	label,
}: {
	active?: boolean;
	payload?: { dataKey: string; value: number; payload?: { dateRange: string; week: string } }[];
	label?: string;
}) {
	const { formatCurrency } = useFormatting();
	if (!active || !payload?.length) return null;
	const forecasted =
		payload.find((p) => p.dataKey === "forecasted")?.value ?? 0;
	const actual = payload.find((p) => p.dataKey === "actual")?.value ?? 0;
	const variance = actual - forecasted;
	const dateRange = payload[0]?.payload?.dateRange;
	const weekLabel = payload[0]?.payload?.week;
	return (
		<div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
			<p className="font-semibold mb-0.5">{weekLabel ?? label}</p>
			{dateRange && (
				<p className="text-xs text-muted-foreground mb-2">{dateRange}</p>
			)}
			<div className="space-y-1">
				<div className="flex gap-4 justify-between">
					<span className="text-muted-foreground">
						{getForecastTerm(true)}:
					</span>
					<span className="font-medium">{formatCurrency(forecasted)}</span>
				</div>
				<div className="flex gap-4 justify-between">
					<span className="text-muted-foreground">Actual:</span>
					<span className="font-medium">{formatCurrency(actual)}</span>
				</div>
				<div
					className={`flex gap-4 justify-between border-t pt-1 mt-1 ${variance >= 0 ? "text-success-600" : "text-destructive"}`}
				>
					<span>Variance:</span>
					<span className="font-semibold">{formatCurrency(variance)}</span>
				</div>
			</div>
		</div>
	);
}

function StatusBadge({
	status,
}: {
	status: "favourable" | "adverse" | "on-track";
}) {
	if (status === "favourable")
		return (
			<Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-xs">
				Favourable
			</Badge>
		);
	if (status === "adverse")
		return (
			<Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 text-xs">
				Adverse
			</Badge>
		);
	return (
		<Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 text-xs">
			On Track
		</Badge>
	);
}

function CategoryRow({
	item,
	formatCurrency,
	hasBudgetData,
}: {
	item: CategoryVarianceResponseApi;
	formatCurrency: (n: number) => string;
	hasBudgetData: boolean;
}) {
	const [expanded, setExpanded] = useState(false);
	const status = getVarianceStatus(item.forecastVariance, item.categoryType);

	return (
		<div className="border rounded-lg overflow-hidden">
			<button
				type="button"
				onClick={() => setExpanded((e) => !e)}
				className="w-full flex items-start gap-4 p-4 hover:bg-muted/40 transition-colors text-left"
			>
				<div className="mt-0.5 flex-shrink-0">
					{status === "favourable" && (
						<TrendingUp className="h-4 w-4 text-success-600" />
					)}
					{status === "adverse" && (
						<TrendingDown className="h-4 w-4 text-destructive" />
					)}
					{status === "on-track" && (
						<Minus className="h-4 w-4 text-slate-400" />
					)}
				</div>

				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2 mb-2 flex-wrap">
						<span className="font-medium text-sm">{item.categoryName}</span>
						<StatusBadge status={status} />
						<Badge variant="outline" className="text-xs">
							{item.categoryType}
						</Badge>
					</div>
					<div
						className={`grid gap-4 text-xs ${hasBudgetData ? "grid-cols-4" : "grid-cols-3"}`}
					>
						<div>
							<span className="text-muted-foreground block">
								{getForecastTerm(true)}
							</span>
							<span className="font-semibold">
								{formatCurrency(item.forecastedAmount)}
							</span>
						</div>
						<div>
							<span className="text-muted-foreground block">Actual</span>
							<span className="font-semibold">
								{formatCurrency(item.actualAmount)}
							</span>
						</div>
						<div>
							<span className="text-muted-foreground block">Variance</span>
							<span
								className={`font-semibold ${status === "favourable" ? "text-success-600" : status === "adverse" ? "text-destructive" : "text-muted-foreground"}`}
							>
								{formatCurrency(item.forecastVariance)}
							</span>
						</div>
						{hasBudgetData && item.budgetAmount !== null && (
							<div>
								<span className="text-muted-foreground block">Budget</span>
								<span className="font-semibold">
									{formatCurrency(item.budgetAmount)}
									{item.budgetVariance !== null && (
										<span
											className={`ml-1 text-xs ${item.budgetVariance >= 0 ? "text-success-600" : "text-destructive"}`}
										>
											({formatCurrency(item.budgetVariance)})
										</span>
									)}
								</span>
							</div>
						)}
					</div>
				</div>

				<div className="flex-shrink-0 text-muted-foreground mt-0.5">
					{expanded ? (
						<ChevronUp className="h-4 w-4" />
					) : (
						<ChevronDown className="h-4 w-4" />
					)}
				</div>
			</button>

			{expanded && (
				<div className="px-4 pb-4 pt-0 bg-muted/20 border-t">
					<div className="flex items-center gap-4 mt-3 text-sm">
						<span className="text-muted-foreground">Accuracy:</span>
						<span className="font-medium">
							{item.forecastAccuracyPercent.toFixed(1)}%
						</span>
					</div>
				</div>
			)}
		</div>
	);
}

interface TakeSnapshotDialogProps {
	open: boolean;
	onClose: () => void;
	onConfirm: (
		name: string,
		description: string,
		forecastPeriod: string,
	) => void;
	saving: boolean;
	defaultPeriod: string;
}

function TakeSnapshotDialog({
	open,
	onClose,
	onConfirm,
	saving,
	defaultPeriod,
}: TakeSnapshotDialogProps) {
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [forecastPeriod, setForecastPeriod] = useState(defaultPeriod);

	useEffect(() => {
		setForecastPeriod(defaultPeriod);
	}, [defaultPeriod]);

	function handleConfirm() {
		if (!name.trim()) return;
		onConfirm(name.trim(), description.trim(), forecastPeriod);
	}

	function handleClose() {
		setName("");
		setDescription("");
		setForecastPeriod(defaultPeriod);
		onClose();
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(o) => {
				if (!o) handleClose();
			}}
		>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Camera className="h-4 w-4" />
						Take {getForecastTerm(true)} Snapshot
					</DialogTitle>
					<DialogDescription>
						Freeze the current {getForecastTerm(false).toLowerCase()} as a
						point-in-time baseline for future variance comparisons.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-2">
					<div className="space-y-1.5">
						<Label htmlFor="snapshot-name">
							Snapshot name <span className="text-destructive">*</span>
						</Label>
						<Input
							id="snapshot-name"
							placeholder='e.g. "Q1 Close", "Pre-budget baseline"'
							value={name}
							onChange={(e) => setName(e.target.value)}
							disabled={saving}
							autoFocus
						/>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="snapshot-description">
							Description{" "}
							<span className="text-muted-foreground text-xs">(optional)</span>
						</Label>
						<Textarea
							id="snapshot-description"
							placeholder="Brief note about why this snapshot was taken..."
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							disabled={saving}
							rows={3}
						/>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="snapshot-period">Snapshot Period</Label>
						<Select
							value={forecastPeriod}
							onValueChange={setForecastPeriod}
							disabled={saving}
						>
							<SelectTrigger id="snapshot-period">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{FORECAST_PERIOD_OPTIONS.map((option) => (
									<SelectItem key={option.key} value={option.key}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<p className="text-xs text-muted-foreground flex items-start gap-1.5">
						<Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
						The snapshot captures today&apos;s{" "}
						{getForecastTerm(false).toLowerCase()} data. Once saved it cannot be
						edited &mdash; only archived.
					</p>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={handleClose} disabled={saving}>
						Cancel
					</Button>
					<Button onClick={handleConfirm} disabled={!name.trim() || saving}>
						<Camera className="h-4 w-4 mr-2" />
						{saving ? "Saving..." : "Take Snapshot"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

interface SnapshotSelectorProps {
	snapshots: ForecastSnapshotResponseApi[];
	selectedId: number | null;
	onSelect: (id: number | null) => void;
	isLoading: boolean;
	analysisPeriodDays: number;
}

function getSnapshotCoverageDays(
	snapshot: ForecastSnapshotResponseApi,
): number {
	if (!snapshot.periodStart || !snapshot.periodEnd) return 0;
	const start = new Date(snapshot.periodStart).getTime();
	const end = new Date(snapshot.periodEnd).getTime();
	return Math.round((end - start) / (1000 * 60 * 60 * 24));
}

function SnapshotSelector({
	snapshots,
	selectedId,
	onSelect,
	isLoading,
	analysisPeriodDays,
}: SnapshotSelectorProps) {

	if (isLoading) {
		return (
			<div className="flex items-center gap-2 text-sm text-muted-foreground border rounded-md px-3 py-2 bg-muted/30">
				<Loader2 className="h-3.5 w-3.5 animate-spin" />
				<span>Loading snapshots...</span>
			</div>
		);
	}

	if (snapshots.length === 0) {
		return (
			<div className="flex items-center gap-2 text-sm text-muted-foreground border rounded-md px-3 py-2 bg-muted/30">
				<Camera className="h-3.5 w-3.5 flex-shrink-0" />
				<span>
					No snapshots saved yet &mdash; take one to enable comparison
				</span>
			</div>
		);
	}

	return (
		<div className="flex items-center gap-2 flex-wrap">
			<span className="text-sm text-muted-foreground whitespace-nowrap">
				Compare against:
			</span>
			<Select
				value={selectedId !== null ? String(selectedId) : "latest"}
				onValueChange={(v) => onSelect(v === "latest" ? null : Number(v))}
			>
				<SelectTrigger className="w-[220px]">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="latest">
						<span className="flex items-center gap-2">
							<span className="h-2 w-2 rounded-full bg-green-500 inline-block" />
							Latest Snapshot
							{snapshots[0] && (
								<span className="text-muted-foreground">
									&mdash; {snapshots[0].name}
								</span>
							)}
						</span>
					</SelectItem>
					{snapshots.slice(1).map((s) => {
						const coverageDays = getSnapshotCoverageDays(s);
						const isPartial = coverageDays < analysisPeriodDays;
						return (
							<SelectItem
								key={s.forecastSnapshotId}
								value={String(s.forecastSnapshotId)}
							>
								<span className="flex items-center gap-2">
									<Camera className="h-3 w-3 text-muted-foreground" />
									{s.name}
									{isPartial && (
										<span className="text-xs text-amber-600 dark:text-amber-400">
											Covers {coverageDays} of {analysisPeriodDays} days
										</span>
									)}
								</span>
							</SelectItem>
						);
					})}
				</SelectContent>
			</Select>

			{selectedId !== null &&
				(() => {
					const snap = snapshots.find(
						(s) => s.forecastSnapshotId === selectedId,
					);
					if (!snap) return null;
					const coverageDays = getSnapshotCoverageDays(snap);
					if (coverageDays >= analysisPeriodDays) return null;
					return (
						<div className="w-full flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-3 py-2 mt-2">
							<AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
							<p className="text-xs text-amber-800 dark:text-amber-300">
								This snapshot only covers {coverageDays} of the{" "}
								{analysisPeriodDays} days in your selected period. Comparison
								values reflect partial data &mdash; they have not been scaled or
								extrapolated.
							</p>
						</div>
					);
				})()}
		</div>
	);
}

function NoSnapshotsState({ onTakeSnapshot }: { onTakeSnapshot: () => void }) {
	return (
		<Card className="border-dashed">
			<CardContent className="flex flex-col items-center justify-center py-16 text-center">
				<Camera className="h-12 w-12 text-muted-foreground/40 mb-4" />
				<h3 className="text-lg font-semibold mb-2">
					No forecast snapshots available
				</h3>
				<p className="text-muted-foreground text-sm max-w-md mb-6">
					Create a forecast snapshot to enable variance analysis. Snapshots
					capture a point-in-time forecast that can be compared against actual
					results.
				</p>
				<PermissionGate permission="manage_forecast_snapshots">
					<Button onClick={onTakeSnapshot}>
						<Camera className="h-4 w-4 mr-2" />
						Take Snapshot
					</Button>
				</PermissionGate>
			</CardContent>
		</Card>
	);
}

function NoSnapshotsMonthEndDisabledState({
	onTakeSnapshot,
}: {
	onTakeSnapshot: () => void;
}) {
	return (
		<Card className="border-dashed border-amber-300 dark:border-amber-700">
			<CardContent className="flex flex-col items-center justify-center py-16 text-center">
				<div className="relative mb-4">
					<Camera className="h-12 w-12 text-muted-foreground/40" />
					<AlertTriangle className="h-5 w-5 text-amber-500 absolute -bottom-1 -right-1" />
				</div>
				<h3 className="text-lg font-semibold mb-2">
					Automatic snapshots are disabled
				</h3>
				<p className="text-muted-foreground text-sm max-w-md mb-2">
					Month-end automatic snapshots have been turned off in System Settings.
					Variance analysis requires at least one snapshot to compare against.
				</p>
				<p className="text-muted-foreground text-sm max-w-md mb-6">
					Take a manual snapshot now, or re-enable automatic snapshots in{" "}
					<span className="font-medium">System Settings → Snapshots</span>.
				</p>
				<PermissionGate permission="manage_forecast_snapshots">
					<Button onClick={onTakeSnapshot}>
						<Camera className="h-4 w-4 mr-2" />
						Take Manual Snapshot
					</Button>
				</PermissionGate>
			</CardContent>
		</Card>
	);
}

interface DeleteSnapshotDialogProps {
	open: boolean;
	snapshot: ForecastSnapshotResponseApi | null;
	onClose: () => void;
	onConfirm: (id: number) => void;
	deleting: boolean;
}

function DeleteSnapshotDialog({
	open,
	snapshot,
	onClose,
	onConfirm,
	deleting,
}: DeleteSnapshotDialogProps) {
	if (!snapshot) return null;

	return (
		<Dialog
			open={open}
			onOpenChange={(o) => {
				if (!o) onClose();
			}}
		>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2 text-destructive">
						<Trash2 className="h-4 w-4" />
						Archive Snapshot
					</DialogTitle>
					<DialogDescription>
						This action cannot be undone. The snapshot will be archived and
						removed from variance analysis comparisons.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-2">
					<div className="rounded-lg border bg-muted/40 px-4 py-3 space-y-1">
						<p className="text-sm font-medium">{snapshot.name}</p>
						<p className="text-xs text-muted-foreground flex items-center gap-1.5">
							<Clock className="h-3 w-3" />
							Taken{" "}
							{new Date(snapshot.dateCreated).toLocaleDateString("en-GB", {
								day: "numeric",
								month: "short",
								year: "numeric",
							})}
						</p>
					</div>
					<div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-3 py-2">
						<AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
						<p className="text-sm text-amber-800 dark:text-amber-300">
							Once archived, the point-in-time baseline this snapshot captured
							cannot be recreated. Any variance analysis that relied on this
							snapshot will need to use a different baseline.
						</p>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={deleting}>
						Cancel
					</Button>
					<Button
						variant="destructive"
						onClick={() => onConfirm(snapshot.forecastSnapshotId)}
						disabled={deleting}
					>
						{deleting ? (
							<Loader2 className="h-4 w-4 mr-2 animate-spin" />
						) : (
							<Trash2 className="h-4 w-4 mr-2" />
						)}
						{deleting ? "Archiving..." : "Archive Snapshot"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function makeDemoVarianceData(periodDays: number): VarianceAnalysisResponseApi {
	const days = periodDays;
	const now = new Date();
	const actuals: VarianceDataPointResponseApi[] = [];
	const forecast: VarianceDataPointResponseApi[] = [];

	for (let i = days - 1; i >= 0; i--) {
		const d = new Date(now);
		d.setDate(d.getDate() - i);
		const dateStr = d.toISOString().split("T")[0] + "T00:00:00Z";
		const isPayDay = d.getDate() === 28;
		const isInvoiceDay = d.getDay() === 1 && i % 7 === 0;

		const forecastIn = isInvoiceDay
			? 38_000
			: d.getDay() === 3
				? 12_000
				: 2_500;
		const forecastOut = isPayDay ? 92_000 : d.getDay() === 2 ? 18_000 : 4_200;
		// Actuals deviate slightly from forecast — realistic variance
		const actualIn = isInvoiceDay ? 34_500 : d.getDay() === 3 ? 11_200 : 2_500;
		const actualOut = isPayDay ? 92_000 : d.getDay() === 2 ? 19_400 : 4_200;

		actuals.push({
			date: dateStr,
			incoming: actualIn,
			outgoing: actualOut,
			net: actualIn - actualOut,
		});
		forecast.push({
			date: dateStr,
			incoming: forecastIn,
			outgoing: forecastOut,
			net: forecastIn - forecastOut,
		});
	}

	const totalActualIn = actuals.reduce((s, a) => s + a.incoming, 0);
	const totalForecastIn = forecast.reduce((s, f) => s + f.incoming, 0);
	const totalVariance = totalActualIn - totalForecastIn;

	return {
		actuals,
		forecast,
		budget: [],
		variance: [],
		categories: [
			{
				categoryName: "Client Invoices",
				categoryType: "inflow",
				actualAmount: Math.round(totalActualIn * 0.72),
				forecastedAmount: Math.round(totalForecastIn * 0.72),
				budgetAmount: null,
				forecastVariance: Math.round((totalActualIn - totalForecastIn) * 0.72),
				budgetVariance: null,
				forecastAccuracyPercent: 94.2,
			},
			{
				categoryName: "Retention Releases",
				categoryType: "inflow",
				actualAmount: Math.round(totalActualIn * 0.18),
				forecastedAmount: Math.round(totalForecastIn * 0.21),
				budgetAmount: null,
				forecastVariance: Math.round(
					totalActualIn * 0.18 - totalForecastIn * 0.21,
				),
				budgetVariance: null,
				forecastAccuracyPercent: 87.5,
			},
			{
				categoryName: "Payroll",
				categoryType: "outflow",
				actualAmount: 92_000,
				forecastedAmount: 92_000,
				budgetAmount: null,
				forecastVariance: 0,
				budgetVariance: null,
				forecastAccuracyPercent: 100,
			},
			{
				categoryName: "Subcontractors",
				categoryType: "outflow",
				actualAmount: 48_200,
				forecastedAmount: 44_000,
				budgetAmount: null,
				forecastVariance: -4_200,
				budgetVariance: null,
				forecastAccuracyPercent: 90.5,
			},
			{
				categoryName: "Materials",
				categoryType: "outflow",
				actualAmount: 31_600,
				forecastedAmount: 29_000,
				budgetAmount: null,
				forecastVariance: -2_600,
				budgetVariance: null,
				forecastAccuracyPercent: 91.8,
			},
			{
				categoryName: "Overheads",
				categoryType: "outflow",
				actualAmount: 18_900,
				forecastedAmount: 19_500,
				budgetAmount: null,
				forecastVariance: 600,
				budgetVariance: null,
				forecastAccuracyPercent: 96.9,
			},
		],
		snapshotInfo: null,
		forecastAccuracyPercent: 91.4,
		totalVariance,
		hasBudgetData: false,
	};
}

const DEMO_SNAPSHOTS_SEED: ForecastSnapshotResponseApi[] = [
	{
		forecastSnapshotId: 1,
		name: "Q4 2025 Close",
		description: "End-of-quarter baseline before January invoicing cycle",
		triggerType: "manual",
		snapshotDate: "2025-12-31T17:00:00Z",
		forecastPeriod: "90d",
		periodStart: "2025-10-01",
		periodEnd: "2025-12-31",
		resolution: "daily",
		dataPointCount: 92,
		startingCash: 62_400,
		minCash: 45_000,
		minCashDate: "2025-11-15",
		endingCash: 235_400,
		avgMonthlyInflow: 161_667,
		avgMonthlyOutflow: 104_000,
		totalInflow: 485_000,
		totalOutflow: 312_000,
		netCashFlow: 173_000,
		coverageMonths: 3,
		confidenceScore: 0.87,
		createdByUserId: "2",
		createdByUserName: "David Chen",
		dateCreated: "2025-12-31T17:00:00Z",
	},
	{
		forecastSnapshotId: 2,
		name: "Pre-budget baseline",
		description: "Snapshot taken before 2026 budget adjustments",
		triggerType: "manual",
		snapshotDate: "2026-01-15T09:30:00Z",
		forecastPeriod: "90d",
		periodStart: "2026-01-01",
		periodEnd: "2026-03-31",
		resolution: "daily",
		dataPointCount: 90,
		startingCash: 235_400,
		minCash: 180_000,
		minCashDate: "2026-02-20",
		endingCash: 406_900,
		avgMonthlyInflow: 173_333,
		avgMonthlyOutflow: 116_167,
		totalInflow: 520_000,
		totalOutflow: 348_500,
		netCashFlow: 171_500,
		coverageMonths: 3,
		confidenceScore: 0.91,
		createdByUserId: "2",
		createdByUserName: "David Chen",
		dateCreated: "2026-01-15T09:30:00Z",
	},
];

interface VarianceAnalysisPageProps {
	onNavigate?: (page: string) => void;
}

export function VarianceAnalysisPage({
	onNavigate,
}: VarianceAnalysisPageProps) {
	const { formatCurrency } = useFormatting();
	const { mode } = useApp();
	const [period, setPeriod] = useState<Period>("30");
	const [selectedSnapshotId, setSelectedSnapshotId] = useState<number | null>(
		null,
	);
	const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [snapshotToDelete, setSnapshotToDelete] =
		useState<ForecastSnapshotResponseApi | null>(null);
	const [demoSnapshots, setDemoSnapshots] =
		useState<ForecastSnapshotResponseApi[]>(DEMO_SNAPSHOTS_SEED);

	const { data: liveSnapshotsData, isLoading: liveSnapshotsLoading } =
		useForecastSnapshots(mode !== "demo");
	const snapshots =
		mode === "demo" ? demoSnapshots : (liveSnapshotsData?.items ?? []);
	const snapshotsLoading = mode === "demo" ? false : liveSnapshotsLoading;

	const forecastTerm = getForecastTerm(true);
	const { in: incomingTerm, out: outgoingTerm } = getCashMovementTerms();

	const {
		data: liveVarianceData,
		isLoading: liveVarianceLoading,
		error: liveVarianceError,
	} = useVarianceAnalysis(Number(period), selectedSnapshotId ?? undefined);

	// In demo mode, bypass the live API entirely and use local seed data
	const demoVarianceData = useMemo(
		() => (mode === "demo" ? makeDemoVarianceData(Number(period)) : null),
		[mode, period],
	);
	const varianceData = mode === "demo" ? demoVarianceData : liveVarianceData;
	const varianceLoading = mode === "demo" ? false : liveVarianceLoading;
	const varianceError = mode === "demo" ? null : liveVarianceError;

	const createSnapshotMutation = useCreateForecastSnapshot();
	const deleteSnapshotMutation = useDeleteForecastSnapshot();
	const { hasPermission } = usePermissions();
	const canDeleteSnapshots = hasPermission("delete_forecast_snapshots");

	const handleDeleteSnapshot = (id: number) => {
		if (mode === "demo") {
			setDemoSnapshots((prev) =>
				prev.filter((s) => s.forecastSnapshotId !== id),
			);
			if (selectedSnapshotId === id) setSelectedSnapshotId(null);
			setDeleteDialogOpen(false);
			setSnapshotToDelete(null);
			toast.success("Snapshot archived", {
				description:
					"The snapshot has been archived and removed from comparisons.",
			});
			return;
		}
		deleteSnapshotMutation.mutate(id, {
			onSuccess: () => {
				if (selectedSnapshotId === id) setSelectedSnapshotId(null);
				setDeleteDialogOpen(false);
				setSnapshotToDelete(null);
				toast.success("Snapshot archived", {
					description:
						"The snapshot has been archived and removed from comparisons.",
				});
			},
			onError: () => {
				toast.error("Failed to archive snapshot", {
					description: "Please try again or contact support.",
				});
			},
		});
	};

	const totalVariance = varianceData?.totalVariance ?? 0;
	const accuracyPercent = varianceData?.forecastAccuracyPercent ?? null;
	const hasBudgetData = varianceData?.hasBudgetData ?? false;

	const overallStatus: "favourable" | "adverse" | "on-track" =
		Math.abs(totalVariance) < 500
			? "on-track"
			: totalVariance > 0
				? "favourable"
				: "adverse";

	const categories = varianceData?.categories ?? [];
	const adverseCount = categories.filter(
		(c) => getVarianceStatus(c.forecastVariance, c.categoryType) === "adverse",
	).length;
	const favourableCount = categories.filter(
		(c) =>
			getVarianceStatus(c.forecastVariance, c.categoryType) === "favourable",
	).length;

	const chartData = useMemo(() => {
		if (!varianceData) return [];
		const days = Number(period);
		const weeks = Math.ceil(days / 7);
		const buckets: { week: string; dateRange: string; forecasted: number; actual: number }[] = [];

		const shortMonths = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

		function formatShortDate(d: Date): string {
			return `${d.getDate()} ${shortMonths[d.getMonth()]}`;
		}

		for (let i = 0; i < weeks; i++) {
			const weekStart = new Date();
			weekStart.setDate(weekStart.getDate() - days + i * 7);
			const weekEnd = new Date();
			weekEnd.setDate(weekEnd.getDate() - days + (i + 1) * 7);
			// weekEnd label: last day of the bucket (inclusive)
			const weekEndLabel = new Date(weekEnd);
			weekEndLabel.setDate(weekEndLabel.getDate() - 1);

			let forecastNet = 0;
			let actualNet = 0;

			varianceData.forecast.forEach((dp) => {
				const d = new Date(dp.date);
				if (d >= weekStart && d < weekEnd) forecastNet += dp.net;
			});
			varianceData.actuals.forEach((dp) => {
				const d = new Date(dp.date);
				if (d >= weekStart && d < weekEnd) actualNet += dp.net;
			});

			const dateRange = `${formatShortDate(weekStart)} – ${formatShortDate(weekEndLabel)}`;

			buckets.push({
				week: `Wk ${i + 1}`,
				dateRange,
				forecasted: Math.round(forecastNet),
				actual: Math.round(actualNet),
			});
		}

		return buckets;
	}, [varianceData, period]);

	const totalActualIn = varianceData
		? varianceData.actuals.reduce((s, a) => s + a.incoming, 0)
		: 0;
	const totalActualOut = varianceData
		? varianceData.actuals.reduce((s, a) => s + a.outgoing, 0)
		: 0;
	const totalForecastIn = varianceData
		? varianceData.forecast.reduce((s, f) => s + f.incoming, 0)
		: 0;
	const totalForecastOut = varianceData
		? varianceData.forecast.reduce((s, f) => s + f.outgoing, 0)
		: 0;

	function handleTakeSnapshot(
		name: string,
		description: string,
		forecastPeriod: string,
	) {
		createSnapshotMutation.mutate(
			{ name, description: description || undefined, forecastPeriod },
			{
				onSuccess: () => {
					toast.success(`Snapshot "${name}" saved`);
					setSnapshotDialogOpen(false);
					if (mode === "demo") {
						setDemoSnapshots((prev) => [
							{
								forecastSnapshotId: Date.now(),
								name,
								description: description || null,
								triggerType: "manual",
								snapshotDate: new Date().toISOString(),
								forecastPeriod: forecastPeriod || "90d",
								periodStart: null,
								periodEnd: null,
								resolution: "daily",
								dataPointCount: 0,
								startingCash: null,
								minCash: null,
								minCashDate: null,
								endingCash: null,
								avgMonthlyInflow: null,
								avgMonthlyOutflow: null,
								totalInflow: null,
								totalOutflow: null,
								netCashFlow: null,
								coverageMonths: null,
								confidenceScore: null,
								createdByUserId: null,
								createdByUserName: "Demo User",
								dateCreated: new Date().toISOString(),
							},
							...prev,
						]);
					}
				},
				onError: (error: Error) => {
					toast.error(
						error.message || "Failed to save snapshot — please try again",
					);
				},
			},
		);
	}

	if (varianceLoading) {
		return (
			<div className="space-y-6 p-6">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">
						Variance Analysis
					</h1>
					<p className="text-muted-foreground mt-1">Loading analysis...</p>
				</div>
				<div className="flex items-center justify-center py-20">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</div>
		);
	}

	if (varianceError || !varianceData) {
		return (
			<div className="space-y-6 p-6">
				<div className="flex items-center justify-between flex-wrap gap-3">
					<div>
						<h1
							className="text-2xl font-bold text-gray-900"
							data-tour="variance"
						>
							Variance Analysis
						</h1>
						<p className="text-muted-foreground mt-1">
							{forecastTerm} vs actual cash flow &mdash; understand what drove
							the difference
						</p>
					</div>
					<div className="flex items-center gap-2">
						<PermissionGate permission="manage_forecast_snapshots">
							<Button
								variant="outline"
								size="sm"
								onClick={() => setSnapshotDialogOpen(true)}
							>
								<Camera className="h-4 w-4 mr-2" />
								Take Snapshot
							</Button>
						</PermissionGate>
					</div>
				</div>
				<NoSnapshotsState onTakeSnapshot={() => setSnapshotDialogOpen(true)} />
				<TakeSnapshotDialog
					open={snapshotDialogOpen}
					onClose={() => setSnapshotDialogOpen(false)}
					onConfirm={handleTakeSnapshot}
					saving={createSnapshotMutation.isPending}
					defaultPeriod="90d"
				/>
			</div>
		);
	}

	if (snapshots.length === 0 && !snapshotsLoading) {
		return (
			<div className="space-y-6 p-6">
				<div className="flex items-center justify-between flex-wrap gap-3">
					<div>
						<h1
							className="text-2xl font-bold text-gray-900"
							data-tour="variance"
						>
							Variance Analysis
						</h1>
						<p className="text-muted-foreground mt-1">
							{forecastTerm} vs actual cash flow &mdash; understand what drove
							the difference
						</p>
					</div>
					<div className="flex items-center gap-2">
						<PermissionGate permission="manage_forecast_snapshots">
							<Button
								variant="outline"
								size="sm"
								onClick={() => setSnapshotDialogOpen(true)}
							>
								<Camera className="h-4 w-4 mr-2" />
								Take Snapshot
							</Button>
						</PermissionGate>
					</div>
				</div>
				<NoSnapshotsState onTakeSnapshot={() => setSnapshotDialogOpen(true)} />
				<TakeSnapshotDialog
					open={snapshotDialogOpen}
					onClose={() => setSnapshotDialogOpen(false)}
					onConfirm={handleTakeSnapshot}
					saving={createSnapshotMutation.isPending}
					defaultPeriod="90d"
				/>
			</div>
		);
	}

	return (
		<div className="space-y-6 p-6">
			<div className="flex items-center justify-between flex-wrap gap-3">
				<div>
					<h1 className="text-2xl font-bold text-gray-900" data-tour="variance">
						Variance Analysis
					</h1>
					<p className="text-muted-foreground mt-1">
						{forecastTerm} vs actual cash flow &mdash; understand what drove the
						difference
					</p>
				</div>
				<div className="flex items-center gap-2 flex-wrap">
					<SnapshotSelector
						snapshots={snapshots}
						selectedId={selectedSnapshotId}
						onSelect={setSelectedSnapshotId}
						isLoading={snapshotsLoading}
						analysisPeriodDays={Number(period)}
					/>

					<Select
						value={period}
						onValueChange={(v) => setPeriod(v as Period)}
					>
						<SelectTrigger className="w-[160px]">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="7">Last 7 Days</SelectItem>
							<SelectItem value="30">Last 30 Days</SelectItem>
							<SelectItem value="90">Last 90 Days</SelectItem>
						</SelectContent>
					</Select>

					<PermissionGate permission="manage_forecast_snapshots">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setSnapshotDialogOpen(true)}
						>
							<Camera className="h-4 w-4 mr-2" />
							Take Snapshot
						</Button>
					</PermissionGate>

					<Button
						variant="outline"
						size="sm"
						onClick={() => exportCategoriesCsv(categories)}
					>
						<Download className="h-4 w-4 mr-2" />
						Export
					</Button>
				</div>
			</div>

			{selectedSnapshotId !== null &&
				(() => {
					const snap = snapshots.find(
						(s) => s.forecastSnapshotId === selectedSnapshotId,
					);
					if (!snap) return null;
					return (
						<div className="flex justify-end">
						<div className="flex items-center gap-2 text-xs border rounded-full px-3 py-1 bg-muted/40 w-fit">
							<Camera className="h-3 w-3 text-muted-foreground flex-shrink-0" />
							<span className="font-medium">{snap.name}</span>
							<span className="text-muted-foreground">&middot;</span>
							<Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
							<span className="text-muted-foreground whitespace-nowrap">
								{formatSnapshotDate(snap.dateCreated)}
							</span>
							<span className="text-muted-foreground">&middot;</span>
							<span className="text-muted-foreground whitespace-nowrap">
								{formatCurrency(snap.netCashFlow ?? 0)} net
							</span>
							<button
								type="button"
								onClick={() => setSelectedSnapshotId(null)}
								className="ml-1 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
								title="Use latest snapshot"
							>
								<X className="h-3 w-3" />
							</button>
							{canDeleteSnapshots && (
								<button
									type="button"
									onClick={() => {
										setSnapshotToDelete(snap);
										setDeleteDialogOpen(true);
									}}
									className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
									title="Archive this snapshot"
								>
									<Trash2 className="h-3 w-3" />
								</button>
							)}
						</div>
						</div>
					);
				})()}

			{mode !== "demo" && varianceData.snapshotInfo && (
				<div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 px-4 py-3">
					<Camera className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
					<p className="text-sm text-blue-800 dark:text-blue-300">
						Comparing actuals against snapshot{" "}
						<span className="font-semibold">
							&quot;{varianceData.snapshotInfo.name}&quot;
						</span>{" "}
						taken on{" "}
						<span className="font-semibold">
							{formatSnapshotDate(varianceData.snapshotInfo.snapshotDate)}
						</span>{" "}
						({varianceData.snapshotInfo.triggerType} &middot;{" "}
						{varianceData.snapshotInfo.forecastPeriod})
					</p>
					{selectedSnapshotId !== null && (
						<button
							type="button"
							onClick={() => setSelectedSnapshotId(null)}
							className="ml-auto text-blue-500 hover:text-blue-700 dark:hover:text-blue-200 transition-colors flex-shrink-0"
							title="Use latest snapshot"
						>
							<X className="h-4 w-4" />
						</button>
					)}
				</div>
			)}

			<div className="grid gap-4 md:grid-cols-4" data-tour="variance-overview">
				{/* Forecast Accuracy */}
				<Card
					style={{
						borderLeft: `3px solid var(${accuracyPercent !== null && accuracyPercent >= 97 ? "--mc-success-border" : "--mc-warning-border"})`,
					}}
				>
					<CardContent className="p-4 flex flex-col justify-between h-full">
						<div className="flex items-start justify-between gap-2">
							<span className="text-base font-medium text-neutral-800">
								{forecastTerm} Accuracy
							</span>
							<div
								className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
								style={{
									backgroundColor: `var(${accuracyPercent !== null && accuracyPercent >= 97 ? "--mc-success-bg" : "--mc-warning-bg"})`,
								}}
							>
								<Target
									className="h-4 w-4"
									style={{
										color: `var(${accuracyPercent !== null && accuracyPercent >= 97 ? "--mc-success-icon" : "--mc-warning-icon"})`,
									}}
								/>
							</div>
						</div>
						<div className="mt-2">
							<div className="text-2xl font-bold text-neutral-900 tracking-tight">
								{accuracyPercent !== null ? `${accuracyPercent.toFixed(1)}%` : "—"}
							</div>
							<div className="text-xs text-muted-foreground mt-1">
								{accuracyPercent === null
									? "No forecast data"
									: accuracyPercent >= 97
										? "Excellent accuracy"
										: accuracyPercent >= 90
											? "Good accuracy"
											: "Needs review"}
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Net Variance */}
				<Card
					style={{
						borderLeft: `3px solid var(${overallStatus === "favourable" ? "--mc-success-border" : overallStatus === "adverse" ? "--mc-warning-border" : "--mc-info-border"})`,
					}}
				>
					<CardContent className="p-4 flex flex-col justify-between h-full">
						<div className="flex items-start justify-between gap-2">
							<span className="text-base font-medium text-neutral-800">
								Net Variance
							</span>
							<div
								className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
								style={{
									backgroundColor: `var(${overallStatus === "favourable" ? "--mc-success-bg" : overallStatus === "adverse" ? "--mc-warning-bg" : "--mc-info-bg"})`,
								}}
							>
								{overallStatus === "favourable" ? (
									<TrendingUp
										className="h-4 w-4"
										style={{ color: "var(--mc-success-icon)" }}
									/>
								) : overallStatus === "adverse" ? (
									<TrendingDown
										className="h-4 w-4"
										style={{ color: "var(--mc-warning-icon)" }}
									/>
								) : (
									<Minus
										className="h-4 w-4"
										style={{ color: "var(--mc-info-icon)" }}
									/>
								)}
							</div>
						</div>
						<div className="mt-2">
							<div className="text-2xl font-bold text-neutral-900 tracking-tight">
								{formatCurrency(totalVariance)}
							</div>
							<div className="text-xs text-muted-foreground mt-1">
								{overallStatus === "favourable"
									? "Favourable"
									: overallStatus === "adverse"
										? "Adverse"
										: "On track"}
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Incoming vs Forecast */}
				<Card style={{ borderLeft: "3px solid var(--mc-success-border)" }}>
					<CardContent className="p-4 flex flex-col justify-between h-full">
						<div className="flex items-start justify-between gap-2">
							<span className="text-base font-medium text-neutral-800">
								{incomingTerm} vs {forecastTerm}
							</span>
							<div
								className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
								style={{ backgroundColor: "var(--mc-success-bg)" }}
							>
								<TrendingUp
									className="h-4 w-4"
									style={{ color: "var(--mc-success-icon)" }}
								/>
							</div>
						</div>
						<div className="mt-2">
							<div className="text-2xl font-bold text-neutral-900 tracking-tight">
								{formatCurrency(totalActualIn)}
							</div>
							<div className="text-xs text-muted-foreground mt-1">
								{forecastTerm}:{" "}
								<span className="font-medium text-foreground">
									{formatCurrency(totalForecastIn)}
								</span>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Outgoing vs Forecast */}
				<Card style={{ borderLeft: "3px solid var(--mc-warning-border)" }}>
					<CardContent className="p-4 flex flex-col justify-between h-full">
						<div className="flex items-start justify-between gap-2">
							<span className="text-base font-medium text-neutral-800">
								{outgoingTerm} vs {forecastTerm}
							</span>
							<div
								className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
								style={{ backgroundColor: "var(--mc-warning-bg)" }}
							>
								<TrendingDown
									className="h-4 w-4"
									style={{ color: "var(--mc-warning-icon)" }}
								/>
							</div>
						</div>
						<div className="mt-2">
							<div className="text-2xl font-bold text-neutral-900 tracking-tight">
								{formatCurrency(totalActualOut)}
							</div>
							<div className="text-xs text-muted-foreground mt-1">
								{forecastTerm}:{" "}
								<span className="font-medium text-foreground">
									{formatCurrency(totalForecastOut)}
								</span>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>

			{adverseCount > 0 && (
				<Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
					<CardContent className="pt-4 pb-4">
						<div className="flex items-start gap-3">
							<AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
							<div className="flex-1">
								<h3 className="font-medium text-amber-900 dark:text-amber-300">
									{adverseCount} adverse variance
									{adverseCount > 1 ? "s" : ""} require
									{adverseCount === 1 ? "s" : ""} attention
								</h3>
								<p className="text-sm text-amber-700 dark:text-amber-400 mt-0.5">
									{categories
										.filter(
											(c) =>
												getVarianceStatus(
													c.forecastVariance,
													c.categoryType,
												) === "adverse",
										)
										.map((c) => c.categoryName)
										.join(", ")}
									{" \u2014 "}review the details below and consider creating an
									action if follow-up is needed.
								</p>
							</div>
							<Button
								variant="outline"
								size="sm"
								className="border-amber-300 text-amber-700 hover:bg-amber-100 dark:text-amber-400 dark:border-amber-700 flex-shrink-0"
								onClick={() => onNavigate?.("actions")}
							>
								Create Action
								<ArrowRight className="h-3 w-3 ml-1" />
							</Button>
						</div>
					</CardContent>
				</Card>
			)}

			<Tabs defaultValue="breakdown">
				<TabsList>
					<PermissionGate permission="view_variance_by_category">
						<TabsTrigger value="breakdown">Category Breakdown</TabsTrigger>
					</PermissionGate>
					<TabsTrigger value="chart">Weekly Chart</TabsTrigger>
					<TabsTrigger value="balance">Balance Reconciliation</TabsTrigger>
				</TabsList>

				<TabsContent
					value="breakdown"
					className="mt-4 space-y-3"
					data-tour="variance-breakdown"
				>
					<div className="flex items-center gap-2 text-sm text-muted-foreground">
						<span>{categories.length} categories</span>
						{favourableCount > 0 && (
							<Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
								{favourableCount} favourable
							</Badge>
						)}
						{adverseCount > 0 && (
							<Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
								{adverseCount} adverse
							</Badge>
						)}
						{categories.filter(
							(c) =>
								getVarianceStatus(c.forecastVariance, c.categoryType) ===
								"on-track",
						).length > 0 && (
							<Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
								{
									categories.filter(
										(c) =>
											getVarianceStatus(c.forecastVariance, c.categoryType) ===
											"on-track",
									).length
								}{" "}
								on track
							</Badge>
						)}
						<span className="text-xs ml-1">
							&mdash; click any row to expand detail
						</span>
					</div>

					{[
						...categories.filter(
							(c) =>
								getVarianceStatus(c.forecastVariance, c.categoryType) ===
								"adverse",
						),
						...categories.filter(
							(c) =>
								getVarianceStatus(c.forecastVariance, c.categoryType) ===
								"on-track",
						),
						...categories.filter(
							(c) =>
								getVarianceStatus(c.forecastVariance, c.categoryType) ===
								"favourable",
						),
					].map((item) => (
						<CategoryRow
							key={`${item.categoryName}-${item.categoryType}`}
							item={item}
							formatCurrency={formatCurrency}
							hasBudgetData={hasBudgetData}
						/>
					))}
				</TabsContent>

				<TabsContent value="chart" className="mt-4">
					<Card>
						<CardHeader>
							<CardTitle className="text-base">
								Net Cash Flow &mdash; {forecastTerm} vs Actual
								{mode !== "demo" && varianceData.snapshotInfo && (
									<span className="ml-2 text-xs font-normal text-muted-foreground">
										(snapshot: {varianceData.snapshotInfo.name})
									</span>
								)}
							</CardTitle>
							<CardDescription>
								Weekly net cash movement (Last {period} Days)
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="h-64">
								<ResponsiveContainer width="100%" height="100%">
									<BarChart
										data={chartData}
										margin={{ top: 5, right: 20, left: 10, bottom: Number(period) <= 30 ? 10 : 5 }}
									>
										<CartesianGrid
											strokeDasharray="3 3"
											stroke="hsl(var(--border))"
											vertical={false}
										/>
										<XAxis
											dataKey={Number(period) <= 30 ? "dateRange" : "week"}
											tick={{
												fontSize: Number(period) <= 30 ? 10 : 12,
												fill: "hsl(var(--muted-foreground))",
											}}
											angle={Number(period) <= 30 ? -35 : 0}
											textAnchor={Number(period) <= 30 ? "end" : "middle"}
											height={Number(period) <= 30 ? 55 : 40}
											axisLine={{ stroke: "hsl(var(--border))" }}
											tickLine={false}
										/>
										<YAxis
											tick={{
												fontSize: 12,
												fill: "hsl(var(--muted-foreground))",
											}}
											axisLine={false}
											tickLine={false}
											tickFormatter={(v: number) =>
												`${getCurrencySymbol()}${(v / 1000).toFixed(0)}k`
											}
										/>
										<Tooltip content={<ChartTooltip />} />
										<Legend />
										<ReferenceLine y={0} stroke="hsl(var(--border))" />
										<Bar
											dataKey="forecasted"
											name={forecastTerm}
											fill="hsl(var(--muted-foreground))"
											opacity={0.5}
											radius={[2, 2, 0, 0]}
										/>
										<Bar
											dataKey="actual"
											name="Actual"
											fill="hsl(var(--primary))"
											radius={[2, 2, 0, 0]}
										/>
									</BarChart>
								</ResponsiveContainer>
							</div>
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent value="balance" className="mt-4">
					<Card>
						<CardHeader>
							<CardTitle className="text-base">
								Balance Reconciliation
								{mode !== "demo" && varianceData.snapshotInfo && (
									<span className="ml-2 text-xs font-normal text-muted-foreground">
										(snapshot: {varianceData.snapshotInfo.name})
									</span>
								)}
							</CardTitle>
							<CardDescription>
								How forecasted and actual cash positions compare over the period
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								<div className="space-y-2">
									<p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
										{forecastTerm}
									</p>
									<div className="flex justify-between text-sm">
										<span className="text-muted-foreground">
											+ {incomingTerm}
										</span>
										<span className="text-success-600 font-medium">
											+{formatCurrency(totalForecastIn)}
										</span>
									</div>
									<div className="flex justify-between text-sm">
										<span className="text-muted-foreground">
											&ndash; {outgoingTerm}
										</span>
										<span className="text-destructive font-medium">
											&ndash;{formatCurrency(totalForecastOut)}
										</span>
									</div>
									<div className="flex justify-between text-sm font-semibold border-t pt-2">
										<span>Net ({forecastTerm})</span>
										<span>
											{formatCurrency(totalForecastIn - totalForecastOut)}
										</span>
									</div>
								</div>

								<div className="space-y-2 pt-2">
									<p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
										Actual
									</p>
									<div className="flex justify-between text-sm">
										<span className="text-muted-foreground">
											+ {incomingTerm}
										</span>
										<span className="text-success-600 font-medium">
											+{formatCurrency(totalActualIn)}
										</span>
									</div>
									<div className="flex justify-between text-sm">
										<span className="text-muted-foreground">
											&ndash; {outgoingTerm}
										</span>
										<span className="text-destructive font-medium">
											&ndash;{formatCurrency(totalActualOut)}
										</span>
									</div>
									<div className="flex justify-between text-sm font-semibold border-t pt-2">
										<span>Net (Actual)</span>
										<span>
											{formatCurrency(totalActualIn - totalActualOut)}
										</span>
									</div>
								</div>

								<div
									className={`flex justify-between text-sm font-semibold border-t pt-3 ${overallStatus === "favourable" ? "text-success-600" : overallStatus === "adverse" ? "text-destructive" : "text-muted-foreground"}`}
								>
									<span>Net Variance</span>
									<span>{formatCurrency(totalVariance)}</span>
								</div>
							</div>
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>

			<TakeSnapshotDialog
				open={snapshotDialogOpen}
				onClose={() => setSnapshotDialogOpen(false)}
				onConfirm={handleTakeSnapshot}
				saving={createSnapshotMutation.isPending}
				defaultPeriod="90d"
			/>

			<DeleteSnapshotDialog
				open={deleteDialogOpen}
				snapshot={snapshotToDelete}
				onClose={() => {
					setDeleteDialogOpen(false);
					setSnapshotToDelete(null);
				}}
				onConfirm={handleDeleteSnapshot}
				deleting={deleteSnapshotMutation.isPending}
			/>
		</div>
	);
}
